/**
 * Settings > Fund config's own rules (IP-98..IP-102, IP-136), kept free of
 * React so node --test covers them (feeConfig.test.js).
 *
 * A fund's fees are percentages only (IP-136): the exit fee a percentage of
 * the buying price, or none where LIV's isExitFeeRequired says the fund
 * charges none; the entry fee a percentage of the amount, which allocation
 * charges (IP-137). IP-99's LKR a unit and IP-100's LKR a subscription are
 * retired, and the page offers neither.
 *
 * A fund's fees change by four eyes (IP-101): one person proposes the exit fee
 * (NONE or a percentage, and the holding period in calendar months), the
 * entry fee (a percentage), the transaction charge rate (IP-179: a percentage
 * of the NAV file's charge base, 0 to 5) and the date they take effect from;
 * the form starts from the row the change replaces. A DIFFERENT
 * person approves or rejects the
 * proposal. Each of the three asks for a fresh authenticator code bound to
 * exactly what is sent (stepup/useStepUp.js). The server decides every rule;
 * what this module does is make sure the page never builds a request it
 * already knows the server will refuse, and says why in the server's words:
 *
 *   - a percentage the person types is turned into the rate the server keeps
 *     in decimal arithmetic (@lfoc/domain/decimal.js), never a float: 1.5 is
 *     0.015000;
 *   - a proposal is put through the catalogue's own normaliser
 *     (FUND_FEES_PROPOSE) and fees/rules.js's date and fee rules - its entry
 *     fee still named PERCENT, the shape the catalogue has kept since 0011 so
 *     a proposal's digest is unchanged (IP-136); the dates
 *     are the server's - the first one every date rule allows
 *     (firstEffectiveDate: after today, the latest row and the latest order
 *     already taken) and the calendar's closed days after it - so the page
 *     never offers, or lets through, a date the server will refuse;
 *   - the exit-fee preview is fees/rules.js's exitFeePriceOf on the latest
 *     buying price, the arithmetic the NAV run uses (IP-99);
 *   - the proposer is never offered the approval or rejection of their own
 *     proposal (the server refuses it, MAKER_IS_CHECKER), and nobody is
 *     offered the approval of one whose effective date has come (the server
 *     refuses it, FEE_EFFECTIVE_DATE_TOO_EARLY: it can only be rejected);
 *   - IP-204: the two Apply fees conditions - a checkbox each, and an LKR
 *     amount a leg's value must be MORE THAN for its box to start ticked -
 *     are always sent (feeDefaults, null for off), and offered only on a fee
 *     the proposal charges (conditionsOffered); the amount is a decimal
 *     string (amountText), never a float;
 *   - the screen's open form or dialog follows the funds as last read
 *     (modalFund, rejectTarget), and a refusal that says the page was stale
 *     closes it, shows the server's words on the page and reads the funds
 *     again (refusalOutcome) - nothing is decided on a copy the server has
 *     moved past.
 */
import { actionOf } from '@lfoc/domain/authority/catalogue.js';
import { cmp, div, sub, toFixed } from '@lfoc/domain/decimal.js';
import {
    DEFAULT_HOLDING_MONTHS, ENTRY_FEE_BASIS, EXIT_FEE_BASIS, MAX_CHARGE_RATE, MAX_EXIT_FEE_RATE, describeChargeRate, describeDefaultCondition,
    describeEntryFee, describeExitFee, describeFeeDefaults, effectiveDateRefusal, exitFeePriceOf, feeRefusal, percentText
} from '@lfoc/domain/fees/rules.js';

export { EXIT_FEE_BASIS, ENTRY_FEE_BASIS, describeChargeRate, describeDefaultCondition, describeEntryFee, describeExitFee, describeFeeDefaults };

/** A percentage as typed: up to 3 whole digits and 4 decimal places (a rate at 6 dp, the columns' scale). */
const PERCENT_TEXT = /^\d{1,3}(\.\d{1,4})?$/;

const typedText = (value) => (value === null || value === undefined ? '' : String(value).trim());

/** '1.5' (per cent) -> '0.015000'; null for anything that is not a percentage with at most 4 decimal places. */
export function percentToRate(value) {
    const typed = typedText(value);
    if (!PERCENT_TEXT.test(typed)) return null;
    return div(typed, '100', 6);
}

/**
 * IP-204: an LKR amount as typed - digits, thousands commas allowed, at most 2
 * decimal places - as the decimal string the server keeps ('100,000' ->
 * '100000.00'); null for anything else, and for 0 (a condition is MORE THAN
 * an amount above 0). Never a float.
 */
export function amountText(value) {
    const typed = typedText(value).replace(/,/g, '');
    if (!/^\d{1,14}(\.\d{1,2})?$/.test(typed)) return null;
    const fixed = toFixed(typed, 2);
    return cmp(fixed, '0') > 0 ? fixed : null;
}

/** '0.015000' -> '1.5': a rate as the percentage the form shows. */
export function rateToPercent(rate) {
    return rate === null || rate === undefined ? null : percentText(rate);
}

/** A date as YYYY-MM-DD, whether the form holds a dayjs or a string; null when none is chosen. */
function dateText(value) {
    if (!value) return null;
    if (typeof value === 'string') return value;
    return typeof value.format === 'function' ? value.format('YYYY-MM-DD') : null;
}

const byValidFrom = (a, b) => (a.validFrom < b.validFrom ? -1 : a.validFrom > b.validFrom ? 1 : 0);

/**
 * The fund_config row a change replaces: the fund's open one - its latest
 * scheduled row, else the one in force today. The approval copies every
 * other column of it (fees/sql.js insertConfigFrom).
 */
export function openConfigOf(fund) {
    const scheduled = [...(fund?.scheduled ?? [])].sort(byValidFrom);
    return scheduled.at(-1) ?? fund?.current ?? null;
}

/**
 * The one exit-fee basis a fund may have (IP-136): a percentage of the buying
 * price where LIV's isExitFeeRequired says it charges one, none where it says
 * not - the flag stays the on/off switch (V-7), so there is nothing to choose.
 */
export function exitBasisOf(fund) {
    return fund?.isExitFeeRequired
        ? { value: EXIT_FEE_BASIS.PERCENT, label: 'A percentage of the buying price' }
        : { value: EXIT_FEE_BASIS.NONE, label: 'None - the fund charges no exit fee (LIV\'s isExitFeeRequired)' };
}

/**
 * The first date the date picker offers and pre-fills: the server's
 * firstEffectiveDate - a business day after today, after the fund's latest
 * row (valid_to is written once) and after the latest dealing date of an
 * order already taken (fees/rules.js firstEffectiveDate, by the fund's own
 * calendar). Null when the calendar holds no business day for it, so nothing
 * can be proposed.
 */
export function firstEffectiveDate(fund) {
    return fund?.firstEffectiveDate ?? null;
}

/**
 * The calendar's word on a date as far as the page knows it: false on a
 * closed day the server listed (nonBusinessDays, up to calendarThrough),
 * true otherwise - a date past what the server sent is the server's to judge.
 */
export function isBusinessDay(fund, date) {
    return !(fund?.nonBusinessDays ?? []).includes(date);
}

/** Whether the date picker offers a date: from the first allowed date, and not on a closed day. */
export function isOfferedDate(fund, date) {
    const first = firstEffectiveDate(fund);
    return Boolean(first && date && date >= first && isBusinessDay(fund, date));
}

/** A fee's value as its form field shows it: a percentage; nothing for no fee (or a retired basis, which has no value). */
function valueField(fee) {
    if (!fee || fee.value === null || fee.value === undefined || fee.basis !== EXIT_FEE_BASIS.PERCENT) return '';
    return rateToPercent(fee.value);
}

/** The Propose form's starting values: the fees of the row the change replaces, for the person to change. */
export function proposalFormOf(fund) {
    const open = openConfigOf(fund);
    const exit = open?.exitFee ?? { basis: EXIT_FEE_BASIS.NONE, value: null, holdingMonths: DEFAULT_HOLDING_MONTHS };
    const exitFeeBasis = exitBasisOf(fund).value;
    return {
        exitFeeBasis,
        exitFeeValue: exitFeeBasis === exit.basis ? valueField(exit) : '',
        exitFeeHoldingMonths: exit.holdingMonths ?? DEFAULT_HOLDING_MONTHS,
        entryFeeValue: valueField(open?.entryFee ?? { basis: ENTRY_FEE_BASIS.PERCENT, value: '0' }),
        // IP-179: the rate in force, for the person to keep or change.
        transactionChargeRate: rateToPercent(open?.transactionChargeRate ?? '0'),
        // IP-204: the Apply fees conditions in force - a checkbox each, and its amount when it is on.
        entryDefaultOn: Boolean(open?.feeDefaults?.entryAbove),
        entryDefaultAbove: open?.feeDefaults?.entryAbove ?? '',
        exitDefaultOn: Boolean(open?.feeDefaults?.exitAbove),
        exitDefaultAbove: open?.feeDefaults?.exitAbove ?? '',
        reason: ''
    };
}

/**
 * IP-204: whether each Apply fees condition can be switched on in the form as
 * it stands - only on a fee the proposal charges: a front-end rate above 0,
 * an exit fee the fund charges (LIV's flag). Otherwise the condition is off
 * and disabled, with the reason as visible text beside it.
 *
 * @returns {{ entry: { enabled: boolean, reason: string|null }, exit: { enabled: boolean, reason: string|null } }}
 */
export function conditionsOffered(fund, form) {
    const entryRate = percentToRate(form?.entryFeeValue);
    const entryCharged = entryRate !== null && cmp(entryRate, '0') > 0;
    const exitCharged = (form?.exitFeeBasis ?? exitBasisOf(fund).value) === EXIT_FEE_BASIS.PERCENT;
    return {
        entry: { enabled: entryCharged, reason: entryCharged ? null : 'No front-end fee, so there is no Apply fees box to tick.' },
        exit: { enabled: exitCharged, reason: exitCharged ? null : 'No exit fee (LIV\'s isExitFeeRequired), so there is no Apply fees box to tick.' }
    };
}

/** The field each of fees/rules.js's refusals is about; null for the proposal as a whole. */
const REFUSAL_FIELD = Object.freeze({
    FEE_EFFECTIVE_DATE_TOO_EARLY: 'effectiveDate',
    FEE_EFFECTIVE_DATE_NOT_BUSINESS_DAY: 'effectiveDate',
    FEE_EFFECTIVE_DATE_NOT_AFTER_SCHEDULED: 'effectiveDate',
    FEE_EFFECTIVE_DATE_ORDERS_PENDING: 'effectiveDate',
    FEE_EXIT_FLAG_MISMATCH: 'exitFeeBasis',
    FEE_CONFIG_UNCHANGED: null
});

const FIELD_LABEL = Object.freeze({
    fundId: 'The fund',
    effectiveDate: 'The effective date',
    exitFeeBasis: 'The exit fee',
    exitFeeValue: 'The exit fee',
    exitFeeHoldingMonths: 'The holding period',
    entryFeeBasis: 'The front-end fee',
    entryFeeValue: 'The front-end fee',
    transactionChargeRate: 'The transaction charge rate',
    'feeDefaults.entryAbove': 'The front-end fee\'s amount',
    'feeDefaults.exitAbove': 'The exit fee\'s amount',
    reason: 'The reason'
});

/** IP-204: the form field a catalogue field is typed in, where the two differ. */
const FORM_FIELD = Object.freeze({ 'feeDefaults.entryAbove': 'entryDefaultAbove', 'feeDefaults.exitAbove': 'exitDefaultAbove' });

/** The catalogue's VALIDATION ('reason: must be at least 3 characters') as a sentence under its field. */
function validationOf(error) {
    const field = error?.details?.field ?? null;
    const words = String(error?.message ?? error).replace(/^[\w.[\]]+: /, '');
    return { error: (FIELD_LABEL[field] ?? 'The proposal') + ' ' + words + '.', field: FIELD_LABEL[field] ? FORM_FIELD[field] ?? field : null };
}

/**
 * FUND_FEES_PROPOSE's params from the Propose form - exactly what the
 * proposer's code is bound to - or the first reason the server would refuse
 * them, with the form field it is about.
 *
 * @param {object} fund a GET settings/fund-config fund
 * @param {{ effectiveDate: object|string|null, exitFeeBasis?: string, exitFeeValue?: string, exitFeeHoldingMonths: number,
 *   entryFeeValue: string, transactionChargeRate: string, reason: string, entryDefaultOn?: boolean, entryDefaultAbove?: string,
 *   exitDefaultOn?: boolean, exitDefaultAbove?: string }} form what the person entered; percentages as percentages, IP-204's
 *   amounts as LKR. The exit basis is the fund's own (exitBasisOf) unless the form names one; the entry basis is always PERCENT
 * @returns {{ params: object } | { error: string, field: string|null }}
 */
export function proposalParams(fund, form) {
    const effectiveDate = dateText(form.effectiveDate);
    if (!effectiveDate) return { error: 'Choose the date the fees take effect from.', field: 'effectiveDate' };
    const earliest = fund?.earliestEffectiveDate ?? null;
    if (!earliest) {
        return { error: 'The dealing calendar holds no business day after today, so no change can take effect yet.', field: 'effectiveDate' };
    }

    const exitFeeBasis = form.exitFeeBasis ?? exitBasisOf(fund).value;
    const body = {
        fundId: fund.fundId,
        effectiveDate,
        exitFeeBasis,
        exitFeeHoldingMonths: form.exitFeeHoldingMonths,
        // IP-136: always a percentage, still named so the params keep 0011's shape (and an open proposal its digest).
        entryFeeBasis: ENTRY_FEE_BASIS.PERCENT,
        reason: form.reason ?? ''
    };
    if (exitFeeBasis === EXIT_FEE_BASIS.PERCENT) {
        const rate = percentToRate(form.exitFeeValue);
        if (rate === null || cmp(rate, '0') <= 0 || cmp(rate, MAX_EXIT_FEE_RATE) > 0) {
            return { error: 'Enter the exit fee as a percentage above 0 and at most 10, such as 1 or 1.5 (at most 4 decimal places).', field: 'exitFeeValue' };
        }
        body.exitFeeValue = rate;
    }
    const entryRate = percentToRate(form.entryFeeValue);
    if (entryRate === null || cmp(entryRate, '1') >= 0) {
        return { error: 'Enter the front-end fee as a percentage below 100, such as 1.5 - 0 is no fee (at most 4 decimal places).', field: 'entryFeeValue' };
    }
    body.entryFeeValue = entryRate;
    const chargeRate = percentToRate(form.transactionChargeRate);
    if (chargeRate === null || cmp(chargeRate, MAX_CHARGE_RATE) > 0) {
        return { error: 'Enter the transaction charge rate as a percentage from 0 to 5, such as 1.12 - 0 is no charge (at most 4 decimal places).',
            field: 'transactionChargeRate' };
    }
    body.transactionChargeRate = chargeRate;

    // IP-204: the Apply fees conditions, always both named - an amount where the box is ticked, null where it is
    // not. A condition on a fee this proposal does not charge is sent as off, as the form shows it.
    const offered = conditionsOffered(fund, { ...form, exitFeeBasis });
    const conditionOf = (on, typed, enabled, field) => {
        if (!enabled || !on) return { value: null };
        const amount = amountText(typed);
        if (amount === null) {
            return { error: 'Enter the amount as LKR above 0, such as 100000 or 100,000.50 (at most 2 decimal places).', field };
        }
        return { value: amount };
    };
    const entryAbove = conditionOf(form.entryDefaultOn, form.entryDefaultAbove, offered.entry.enabled, 'entryDefaultAbove');
    if (entryAbove.error) return entryAbove;
    const exitAbove = conditionOf(form.exitDefaultOn, form.exitDefaultAbove, offered.exit.enabled, 'exitDefaultAbove');
    if (exitAbove.error) return exitAbove;
    body.feeDefaults = { entryAbove: entryAbove.value, exitAbove: exitAbove.value };

    let params;
    try {
        params = actionOf('FUND_FEES_PROPOSE').params(body);
    } catch (error) {
        return validationOf(error);
    }

    // The server's own rules, in its words. The calendar is the server's: its
    // closed days after today (nonBusinessDays), and the first date every date
    // rule allows (firstEffectiveDate) - a business date before that, after
    // today and after the latest row, is one an order already taken deals on.
    const open = openConfigOf(fund);
    const first = firstEffectiveDate(fund);
    const refusal = effectiveDateRefusal({ effectiveDate, earliest, businessDay: isBusinessDay(fund, effectiveDate), latestValidFrom: open?.validFrom ?? null })
        ?? (first && effectiveDate < first
            ? { code: 'FEE_EFFECTIVE_DATE_ORDERS_PENDING', message: 'Transactions already taken for ' + fund.fundId + ' deal on or after ' + effectiveDate
                + ', on the fees in force when they were taken. Fees can take effect from ' + first + ' at the earliest.' }
            : null)
        ?? (open
            ? feeRefusal(params, {
                isExitFeeRequired: fund.isExitFeeRequired === true,
                // IP-204: a row read without conditions has none (the server sends feeDefaults on every row since 0039).
                current: { exitFee: open.exitFee, entryFee: open.entryFee, defaults: open.feeDefaults ?? { entryAbove: null, exitAbove: null } },
                currentChargeRate: open.transactionChargeRate ?? null
            })
            : null);
    if (refusal) return { error: refusal.message, field: REFUSAL_FIELD[refusal.code] ?? null };
    return { params };
}

/**
 * What the exit fee being entered makes of the latest buying price (IP-99):
 * the price a young unit is paid (bwef) and the fee a unit. Only a preview -
 * a percentage of at most 10 % is never held to a price (IP-136). Nothing to
 * show without a fee, a usable rate or a price.
 *
 * @returns {{ dealingDate: string|null, buyingPrice: string|null, exitFeePrice: string|null, feePerUnit: string|null }}
 */
export function exitPreview(fund, form) {
    const price = fund?.latestPrice ?? null;
    const none = { dealingDate: price?.dealingDate ?? null, buyingPrice: price?.buyingPrice ?? null, exitFeePrice: null, feePerUnit: null };
    if (!price?.buyingPrice || (form?.exitFeeBasis ?? exitBasisOf(fund).value) !== EXIT_FEE_BASIS.PERCENT) return none;
    const rate = percentToRate(form?.exitFeeValue);
    if (rate === null || cmp(rate, '0') <= 0 || cmp(rate, MAX_EXIT_FEE_RATE) > 0) return none;
    const exitFeePrice = exitFeePriceOf(price.buyingPrice, { basis: EXIT_FEE_BASIS.PERCENT, value: rate });
    return { ...none, exitFeePrice, feePerUnit: toFixed(sub(price.buyingPrice, exitFeePrice), 4) };
}

/**
 * Why the fund's Propose is not offered, in words shown beside the button (a
 * disabled button takes no focus, so a tooltip on it is never read); null
 * when it is.
 */
export function proposeBlockedReason(fund) {
    if (fund.openProposal) return 'A change is already proposed; it must be approved or rejected first.';
    if (fund.status === 'CLOSED') return fund.fundId + ' is closed.';
    if (!openConfigOf(fund)) return fund.fundId + ' has no dealing rules to change.';
    if (!firstEffectiveDate(fund)) return 'The dealing calendar holds no business day to change the fees from; load it first.';
    return null;
}

/** What the Propose form calls the row the change replaces: "Now", or "From <date>" for a row still to come. */
export function replacedRowLabel(fund) {
    const open = openConfigOf(fund);
    if (!open) return 'Now';
    return open === fund.current || open.configId === fund.current?.configId ? 'Now' : 'From ' + open.validFrom;
}

/**
 * Why a proposal whose date has come can only be rejected, and who can do
 * it - in words for the person reading it: the proposer cannot reject their
 * own, and Propose stays closed while it is open.
 */
export function lapsedText({ own, canReject }) {
    const lead = 'Its effective date has come, so it can no longer be approved. ';
    if (own) return lead + 'Another person with Approve fund fees must reject it; you can then propose again.';
    if (canReject) return lead + 'Reject it; a new proposal can then be made.';
    return lead + 'A person with Approve fund fees must reject it before a new proposal can be made.';
}

/** True when the signed-in person proposed it: they may neither approve nor reject it. */
export function isOwnProposal(proposal, uid) {
    if (!proposal?.proposedBy || !uid) return false;
    return String(proposal.proposedBy).toLowerCase() === String(uid).toLowerCase();
}

/** True when an open proposal's effective date has come: the approval would be refused, so it can only be rejected. */
export function hasLapsed(fund, proposal) {
    const earliest = fund?.earliestEffectiveDate ?? null;
    return Boolean(proposal && proposal.state === 'PROPOSED' && earliest && proposal.effectiveDate < earliest);
}

/**
 * True when the server's refusal of a fee action says the page's copy was out
 * of date - the proposal decided, or another proposed, by someone else; a row
 * scheduled since; the day moved on past an effective date; a price published
 * (a 404 or a 409) - so the screen reads the funds again behind the message.
 * A wrong code (401), a missing claim (403) or a bad request (400) is not.
 */
export function isStale(error) {
    return error?.status === 404 || error?.status === 409;
}

/**
 * What the screen does with a refused fee action: a stale refusal (isStale)
 * closes the open form or dialog - it was built on a copy the server has
 * moved past - shows the server's words on the page and reads the funds
 * again; any other refusal stays in the form, where the person can act on it.
 *
 * @returns {{ closeModal: boolean, reload: boolean, showOnPage: boolean }}
 */
export function refusalOutcome(error) {
    const stale = isStale(error);
    return { closeModal: stale, reload: stale, showOnPage: stale };
}

/**
 * The fund the Propose form is for, as last read - or null when it may no
 * longer be proposed for (gone, closed, or someone else proposed first), so
 * the form closes. The screen keeps only the fund's id.
 */
export function modalFund(funds, fundId) {
    const fund = (funds ?? []).find((f) => f.fundId === fundId) ?? null;
    return fund && !proposeBlockedReason(fund) ? fund : null;
}

/**
 * The fund and proposal the Reject dialog is for, as last read - or null when
 * the proposal is no longer the fund's open one (decided by someone else), so
 * the dialog closes. The screen keeps only the two ids.
 */
export function rejectTarget(funds, ids) {
    if (!ids) return null;
    const fund = (funds ?? []).find((f) => f.fundId === ids.fundId) ?? null;
    const proposal = fund?.openProposal ?? null;
    return proposal && proposal.proposalId === ids.proposalId ? { fund, proposal } : null;
}

/** FUND_FEES_APPROVE's params: the proposal, and the digest of the fees its proposer's code was bound to. */
export function approveParams(proposal) {
    return actionOf('FUND_FEES_APPROVE').params({ proposalId: proposal.proposalId, payloadSha256: proposal.payloadSha256 });
}

/** FUND_FEES_REJECT's params, or why the reason will not do (3 to 500 characters on one line). */
export function rejectParams(proposal, reason) {
    try {
        return { params: actionOf('FUND_FEES_REJECT').params({ proposalId: proposal.proposalId, reason: reason ?? '' }) };
    } catch (error) {
        return validationOf(error);
    }
}
